/**
 * NeighborCard Component
 * 
 * Displays a single neighbor parcel with rich details.
 */

import React from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { color, space, text, radius } from '../../../shared/tokens/mobile';
import { formatCurrency, formatPricePerAcre } from '../../../shared/utils/formatters';

interface NeighborCardProps {
  neighbor: any; // Graph API returns flat structure
  tier: number;
  isLast: boolean;
  showInstantBadge: boolean;
  onPress: (parcelId: string) => void;
}

export function NeighborCard({ neighbor, tier, isLast, showInstantBadge, onPress }: NeighborCardProps) {
  const parseNum = (val: any): number | null => {
    if (val == null) return null;
    const num = typeof val === 'string' ? parseFloat(val) : val;
    return isNaN(num) ? null : num;
  };
  
  const acres = parseNum(neighbor.area_acres);
  const totalValue = parseNum(neighbor.total_value);
  const sharedBoundaryFeet = parseNum(neighbor.shared_boundary_feet);

  return (
    <Pressable
      style={[styles.card, isLast && styles.cardLast]}
      onPress={() => neighbor.parcel_id && onPress(neighbor.parcel_id)}
    >
      <View style={styles.main}>
        <View style={styles.header}>
          <Text style={styles.parcelId}>{neighbor.parcel_id || 'N/A'}</Text>
          {showInstantBadge && (
            <View style={styles.instantBadge}>
              <Ionicons name="flash" size={10} color={color.success} />
              <Text style={styles.instantBadgeText}>Instant</Text>
            </View>
          )}
        </View>
        <Text style={styles.owner} numberOfLines={1}>
          {neighbor.owner_name || 'Unknown Owner'}
        </Text>
        {neighbor.site_address && (
          <Text style={styles.address} numberOfLines={1}>
            {neighbor.site_address}
          </Text>
        )}

        <View style={styles.details}>
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Acres:</Text>
            <Text style={styles.detailValue}>{acres != null ? acres.toFixed(2) : '—'}</Text>
          </View>
          {totalValue != null && totalValue > 0 && (
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Tax Value:</Text>
              <Text style={styles.detailValue}>{formatCurrency(totalValue)}</Text>
            </View>
          )}
          {totalValue != null && totalValue > 0 && acres != null && acres > 0 && (
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>$/Acre:</Text>
              <Text style={styles.detailValue}>{formatPricePerAcre(totalValue, acres)}</Text>
            </View>
          )}
          {tier === 1 && sharedBoundaryFeet != null && sharedBoundaryFeet > 0 && (
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Shared Boundary:</Text>
              <Text style={styles.detailValue}>{Math.round(sharedBoundaryFeet).toLocaleString()} ft</Text>
            </View>
          )}
          {neighbor.land_use_description && (
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Land Use:</Text>
              <Text style={styles.detailValue} numberOfLines={1}>{neighbor.land_use_description}</Text>
            </View>
          )}
          {(parseNum(neighbor.land_value) != null || parseNum(neighbor.building_value) != null) && (
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Land/Building:</Text>
              <Text style={styles.detailValue}>
                {formatCurrency(parseNum(neighbor.land_value) ?? 0)} / {formatCurrency(parseNum(neighbor.building_value) ?? 0)}
              </Text>
            </View>
          )}
        </View>
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  card: {
    paddingHorizontal: space.md,
    paddingVertical: space.md,
    borderBottomWidth: 1,
    borderBottomColor: color.divider,
  },
  cardLast: {
    borderBottomWidth: 0,
  },
  main: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: space.xs,
  },
  parcelId: {
    fontSize: text.caption.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    color: color.primary,
  },
  instantBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    backgroundColor: color.success + '15',
    paddingHorizontal: space.xs,
    paddingVertical: 2,
    borderRadius: radius.sm,
  },
  instantBadgeText: {
    fontSize: 9,
    color: color.success,
    fontWeight: '600',
  },
  owner: {
    fontSize: text.body.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    color: color.textPrimary,
    marginTop: 2,
  },
  address: {
    fontSize: text.caption.fontSize,
    color: color.textMuted,
    marginTop: 2,
  },
  details: {
    marginTop: space.sm,
    paddingTop: space.sm,
    borderTopWidth: 1,
    borderTopColor: color.divider,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 2,
  },
  detailLabel: {
    fontSize: text.caption.fontSize,
    color: color.textMuted,
  },
  detailValue: {
    fontSize: text.caption.fontSize,
    color: color.textPrimary,
    fontWeight: text.bodyMedium.fontWeight,
    flex: 1,
    textAlign: 'right',
    marginLeft: space.sm,
  },
});

